//  Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { D3eService } from '../../../../services/d3e-service';
import { HeaderService } from '../../../header.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NotificationService } from '../../../../services/notification-service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { WindowService } from '../../../../services/window.service';
import { IIntegration, PartnerTypes } from '../../../../types/integrations';
import { SelectedOrgMethod } from '../../../../shared/selectedOrg';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-integration-testing',
    templateUrl: './integration-testing.component.html',
    styleUrls: ['./integration-testing.component.scss'],
    standalone: false
})
export class IntegrationTestingComponent implements OnInit {

  private readonly PARTNER_ID = PartnerTypes.CIGENT_TESTING;
  formGroup: UntypedFormGroup;
  integration: IIntegration;
  integrationId: string = null;
  loading = true;

  selectedOrgId: string;
  hasUserBeenSet = false;

  constructor( private route: ActivatedRoute,
    private d3eService: D3eService,
    private router: Router,
    private headerService: HeaderService,
    private formBuilder: UntypedFormBuilder,
    private notification: NotificationService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private windowService: WindowService) {

      this.headerService.setTitle(this.translate.instant('integrations.testing.title'));
      this.headerService.setSubtitle(this.translate.instant('integrations.testing.subtitle'));
      this.headerService.setIcon('mdi-sync');

      this.formGroup = this.formBuilder.group({
        device_name: ['', Validators.compose([Validators.required])],
        lock_option: ['', Validators.required],
        admin_unlock: [{value: false, disabled: true}],
        send_at_start: [''],
        send_every_loop: ['']
      });
  }


  ngOnInit() {

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
            // TODO: launch the dirty flag window
            this.router.navigateByUrl('/integrations');
        }
        this.hasUserBeenSet = true;
    });

    this.route.params.subscribe(params => {
      this.integrationId = params.id;
      if (this.integrationId === 'new') {
        this.loading = false;
      } else {
        const selectedUser = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.get_integration(this.integrationId, selectedUser)
          .pipe(untilDestroyed(this))
          .subscribe(integration => {
            if (integration.partner_id !== this.PARTNER_ID) {
              this.router.navigateByUrl('/integrations');
            }
            this.formGroup.controls['device_name'].setValue(integration.config.device_name);
            this.formGroup.controls['lock_option'].setValue(integration.config.lock_option);
            this.formGroup.controls['admin_unlock'].setValue(integration.config.admin_unlock);
            this.formGroup.controls['send_at_start'].setValue(integration.config.send_at_start);
            this.formGroup.controls['send_every_loop'].setValue(integration.config.send_every_loop);

            this.integration = integration;
        });
      }
    });
  }


  reportSaveSuccess() {
    this.notification.showSuccess(this.translate.instant('CONFIGURATION_SAVED'));
  }

  reportSaveError() {
    this.notification.showError(this.translate.instant('REPORT_SAVE_ERROR'));
  }

  goToIntegrations() {
    this.router.navigateByUrl('/integrations');
  }

  cancel() {
    this.goToIntegrations();
  }

  save() {
    this.notification.showUpdate(this.translate.instant('CHANGES_BEING_SAVED'));
    const data = this.formGroup.value;
    if (this.integrationId === 'new') {
      const integration: IIntegration = {
        display_name: 'Cigent Testing',
        enabled: true,
        partner_id: PartnerTypes.CIGENT_TESTING,
        config: {
          device_name: data.device_name,
          admin_unlock: data.admin_unlock || false,
          lock_option: data.lock_option,
          send_at_start: data.send_at_start,
          send_every_loop: data.send_every_loop
        }
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.add_integration(integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    } else {
      this.integration.display_name = 'Cigent Test Integration';
      this.integration.config = {
        device_name: data.device_name,
        admin_unlock: data.admin_unlock || false,
        lock_option: data.lock_option,
        send_at_start: data.send_at_start,
        send_every_loop: data.send_every_loop
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.update_integration(this.integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    }
  }

  onEnabledChanged(id: string, $event: MatSlideToggleChange) {
    const value = $event.checked;

    this.formGroup.controls[id].setValue(value);
  }
}
